import { scaleLinear } from '@visx/scale';

export const sliderValuePercentage = (
  value: number,
  minRange: number,
  maxRange: number
) => {
  const scale = scaleLinear({
    domain: [minRange, maxRange],
    range: [0, 100],
    clamp: true,
  });

  return scale(value);
};

export type SliderTrackBandInput = {
  trackColor?: string;
  label?: string;
  /**
   * This band will stretch from previous threshold all the way to this threshold.
   */
  threshold: number;
  align?: 'left' | 'center' | 'right';
};

export type SliderTrackBand = Required<SliderTrackBandInput>;

export const sliderNormaliseBands = (
  bands: SliderTrackBandInput[] | undefined,
  minRange: number,
  maxRange: number,
  defaultTrackColor: string
): SliderTrackBand[] => {
  if (!bands || !bands.length) return [];

  return bands.map(({ trackColor, threshold, align, label }) => ({
    align: align || 'center',
    label: label || '',
    trackColor: trackColor || defaultTrackColor,
    threshold: sliderValuePercentage(threshold, minRange, maxRange),
  }));
};

/**
 * Note: this function does not take into consideration if we provided sorted or deduplicated thresholds
 * since these are static values provided by developers, it relies that it's already sorted and deduplicated.
 */
export const sliderTrackBandColor = (normalisedBands: SliderTrackBand[]) => {
  if (!normalisedBands.length) return;

  const interlacedBands = normalisedBands.reduce<string[]>(
    (gradients, gradient, index, arr) => {
      const nextBand = arr[index + 1];
      return [
        ...gradients,
        index === 0 && `${gradient.trackColor} 0%`,
        `${gradient.trackColor} ${gradient.threshold}%`,
        nextBand && `${nextBand.trackColor} ${gradient.threshold}%`,
      ].filter(Boolean) as string[];
    },
    []
  );

  return `linear-gradient(to right, ${interlacedBands.join(', ')})`;
};

export const sliderTrackGridTemplateColumns = (
  normalisedBands: SliderTrackBand[]
) => {
  if (!normalisedBands.length) return 'auto';

  const templateColumns = normalisedBands.map((band, index, arr) => {
    const previousBand = arr[index - 1];
    return `${band.threshold - (previousBand?.threshold ?? 0)}%`;
  });

  return templateColumns.join(' ');
};
